import { Button, Flex, Spin, Table, type TableProps } from 'antd'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import { InviteeService } from '@services/InviteeService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { Invitee } from '../../../../types'

dayjs.extend(relativeTime)

export const InviteeTable = ({ invitees, refetch }: { invitees: Invitee[]; refetch: () => Promise<void> }) => {
    const confirm = useConfirmation()
    const { showNotification } = useNotifications()

    const columns: TableProps<Invitee>['columns'] = [
        {
            title: 'Email',
            dataIndex: 'email'
        },
        {
            title: 'School',
            key: 'schools',
            render: (_, { claims }) => {
                return (
                    <>
                        {claims?.map(claim => {
                            if (claim.school) {
                                return (
                                    <a href={`/schools/${claim.school.id}`} key={claim.id}>
                                        {claim.school.name}
                                    </a>
                                )
                            }
                            return null
                        })}
                    </>
                )
            }
        },
        {
            title: 'Role',
            key: 'role',
            render: (_, { claims }) => {
                return (
                    <>
                        {claims?.map(claim => {
                            if (claim.role) {
                                return <p key={claim.id}>{claim.role}</p>
                            }
                            return null
                        })}
                    </>
                )
            }
        },
        {
            title: 'Expires',
            key: 'expires',
            render: (_, { expiry }) => <>{dayjs(expiry).format('MMMM D, YYYY h:mma')}</>
        },
        {
            title: 'Last Sent',
            key: 'lastSent',
            render: (_, { lastSent }) => <>{dayjs(lastSent).fromNow()}</>
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, { id: inviteeId }) => (
                <Flex gap="middle" align="center">
                    <Button
                        type="link"
                        onClick={async () => {
                            if (
                                await confirm({
                                    title: 'Do you want to resend the invite?',
                                    description: 'This will also reset the expiry date'
                                })
                            ) {
                                try {
                                    await InviteeService.resendInvite(inviteeId)
                                    showNotification({ title: 'Invite sent!' })
                                    refetch()
                                } catch (err) {
                                    showNotification({ title: 'Failed to resend invite', type: 'error' })
                                }
                            }
                        }}
                    >
                        Resend Invite
                    </Button>
                </Flex>
            )
        }
    ]

    if (!invitees) return <Spin />

    return (
        <>
            <Flex vertical gap={20}>
                <Table columns={columns} dataSource={invitees || []} rowKey="id" pagination={{ pageSize: 50 }} />
            </Flex>
        </>
    )
}
